/**
 * Parses a Scryfall mana_cost string into discrete pip tokens (TNK-121).
 *
 * Scryfall returns mana costs as bracketed glyphs concatenated end-to-end:
 *   "{2}{U}{U}"     — Counterspell-style: 2 generic, two blue
 *   "{X}{R}"        — variable + 1 red
 *   "{2/W}"         — hybrid generic/colored — one or two life
 *   "{U/R}"         — hybrid colored — pay either
 *   "{U/P}"         — Phyrexian — pay U or 2 life
 *   ""              — Lands and other no-cost cards
 *
 * The returned array is the inner text of each `{...}` group, in print
 * order. The web's `<ManaCost />` component maps each token onto a styled
 * pip glyph — single-character W/U/B/R/G/C get their canonical MTG color,
 * digits + X get a neutral grey, hybrids split the pip background, and
 * Phyrexian gets a darker outline.
 *
 * Tolerant by design: malformed input (missing brace, unknown letters)
 * returns whatever clean tokens were extractable rather than throwing,
 * so a stray Scryfall edge case doesn't crash the shop card detail page.
 */

/** Capture every `{...}` token; lazy match so a stray brace doesn't swallow
 *  the rest of the string. */
const PIP_RE = /\{([^{}]+)\}/g;

export function parseManaCost(cost: string | null | undefined): string[] {
  if (!cost) return [];
  const out: string[] = [];
  for (const m of cost.matchAll(PIP_RE)) {
    const token = m[1].trim();
    if (token.length > 0) out.push(token);
  }
  return out;
}

/** Classification used by the renderer to pick a visual treatment. */
export type ManaPipKind =
  | "white"
  | "blue"
  | "black"
  | "red"
  | "green"
  | "colorless"
  | "generic"
  | "variable"
  | "hybrid"
  | "phyrexian"
  | "snow"
  | "unknown";

/** Maps a single pip token (the inner text between `{}`) onto a kind tag.
 *  Hybrid + Phyrexian are detected by the `/` separator and the `P` suffix
 *  respectively, before the single-letter color check. */
export function manaPipKind(token: string): ManaPipKind {
  const t = token.trim().toUpperCase();
  if (t.length === 0) return "unknown";
  if (t.endsWith("/P")) return "phyrexian";
  if (t.includes("/")) return "hybrid";
  if (/^\d+$/.test(t)) return "generic";
  if (t === "X" || t === "Y" || t === "Z") return "variable";
  if (t === "S") return "snow";
  if (t === "W") return "white";
  if (t === "U") return "blue";
  if (t === "B") return "black";
  if (t === "R") return "red";
  if (t === "G") return "green";
  if (t === "C") return "colorless";
  return "unknown";
}
